import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import api from '../services/api';
import DateField from './DateField';
import { formatDate } from '../utils/dateUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Phase 5: All cost class history is in the unified cost_class_history table
// keyed by cat_id. Every entity type (active/adopted/freed/angel) now maps to
// the same table since the cat's id is used directly as the entityId.
const TABLE_CONFIG: Record<string, { table: string; fkColumn: string }> = {
  cats:    { table: 'cost_class_history', fkColumn: 'cat_id' },
  angels:  { table: 'cost_class_history', fkColumn: 'cat_id' },
  adopted: { table: 'cost_class_history', fkColumn: 'cat_id' },
  freed:   { table: 'cost_class_history', fkColumn: 'cat_id' },
};

interface HistoryEntry {
  id: number;
  cost_class: string;
  effective_from: string;
  notes?: string;
}

interface Props {
  entityId: number;
  entityTable: 'cats' | 'angels' | 'adopted' | 'freed';
  /** Called after any mutation so the parent can reload the entity if needed */
  onHistoryChanged?: () => void;
}

const COST_CLASSES = ['Ordinary', 'Increased', 'Increased Kitten', 'Increased Sickroom', 'Other'];

const COST_CLASS_COLORS: Record<string, string> = {
  'Ordinary':          '#90a4ae',
  'Increased':         '#ffb300',
  'Increased Kitten':  '#26a69a',
  'Increased Sickroom':'#ef5350',
  'Other':             '#ab47bc',
};

const CostClassHistoryPanel: React.FC<Props> = ({ entityId, entityTable, onHistoryChanged }) => {
  const { t } = useTranslation();

  const translateCostClass = (cc: string): string => {
    const map: Record<string, string> = {
      'Ordinary': t('cat.costClasses.ordinary'),
      'Increased': t('cat.costClasses.increased'),
      'Increased Kitten': t('cat.costClasses.increasedKitten'),
      'Increased Sickroom': t('cat.costClasses.increasedSickroom'),
      'Other': t('cat.costClasses.other'),
    };
    return map[cc] || cc;
  };

  const translateNote = (note: string): string => {
    const knownNotes: Record<string, string> = {
      'Seeded from initial cost_class at migration': t('cat.costClassHistory.seededNote'),
      'Restored after temporary period': t('cat.costClassHistory.restoredAfterPeriod'),
    };
    return knownNotes[note] || note;
  };
  const config = TABLE_CONFIG[entityTable];

  const [entries, setEntries] = useState<HistoryEntry[]>([]);

  const segments = useMemo(() => {
    if (entries.length === 0) return [];
    const today = new Date().toISOString().split('T')[0];
    return entries.map((entry, idx) => {
      const from = entry.effective_from;
      const nextEntry = entries[idx + 1];
      const until = nextEntry
        ? new Date(new Date(nextEntry.effective_from).getTime() - 86400000).toISOString().split('T')[0]
        : today;
      const isOpen = !nextEntry;
      const days = Math.max(1, Math.round(
        (new Date(until).getTime() - new Date(from).getTime()) / 86400000 + 1
      ));
      return { cost_class: entry.cost_class, from, until, days, isOpen };
    });
  }, [entries]);

  const totalDays = useMemo(() => segments.reduce((s, seg) => s + seg.days, 0), [segments]);
  const [loading, setLoading] = useState(false);

  // Add-entry dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newCostClass, setNewCostClass] = useState('Ordinary');
  const [newEffectiveFrom, setNewEffectiveFrom] = useState('');
  const [newEffectiveUntil, setNewEffectiveUntil] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Edit-entry dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editEntry, setEditEntry] = useState<HistoryEntry | null>(null);
  const [editPrevEntry, setEditPrevEntry] = useState<HistoryEntry | null>(null);
  const [editNextEntry, setEditNextEntry] = useState<HistoryEntry | null>(null);
  const [editNextNextEntry, setEditNextNextEntry] = useState<HistoryEntry | null>(null);
  const [editCostClass, setEditCostClass] = useState('Ordinary');
  const [editEffectiveFrom, setEditEffectiveFrom] = useState('');
  const [editUntil, setEditUntil] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const loadHistory = useCallback(async (): Promise<HistoryEntry[]> => {
    setLoading(true);
    try {
      const res = await api.get(
        `${API_URL}/api/cost-class-history/${config.table}/${config.fkColumn}/${entityId}`
      );
      const data: Array<{ id: number; cost_class: string; effective_from: string; notes?: string }> = res.data;
      const loaded = data.map((row) => ({
        id: row.id,
        cost_class: row.cost_class,
        effective_from: row.effective_from,
        notes: row.notes ?? undefined,
      }));
      setEntries(loaded);
      return loaded;
    } catch (e) {
      console.error('Failed to load cost class history', e);
      return [];
    } finally {
      setLoading(false);
    }
  }, [entityId, config]);

  // Derive the currently-effective cost class from the history and write it
  // back to the main entity table so the two stay in sync.
  const syncCostClassToEntity = useCallback(async (updatedEntries: HistoryEntry[]) => {
    const today = new Date().toISOString().split('T')[0];
    const effective = [...updatedEntries]
      .filter((e) => e.effective_from <= today)
      .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0];
    if (!effective) return;
    try {
      await api.put(`${API_URL}/api/records`, {
        table: 'cats',
        schema: 'public',
        record_id: entityId,
        id_column: 'id',
        data: { cost_class: effective.cost_class },
      });
    } catch (e) {
      console.error('Failed to sync cost class to entity', e);
    }
  }, [entityTable, entityId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleAdd = async () => {
    if (!newEffectiveFrom || !newCostClass) return;
    if (newEffectiveUntil && newEffectiveUntil <= newEffectiveFrom) {
      setError(t('cat.costClassHistory.endBeforeStart'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`${API_URL}/api/records`, {
        table: config.table,
        schema: 'public',
        data: {
          [config.fkColumn]: entityId,
          cost_class: newCostClass,
          effective_from: newEffectiveFrom,
          notes: newNotes || null,
        },
      });

      // If an explicit end date is given, insert a restoration entry the next day
      // unless an entry already exists at that date (period already bounded)
      if (newEffectiveUntil) {
        const nextDay = new Date(newEffectiveUntil);
        nextDay.setDate(nextDay.getDate() + 1);
        const nextDayStr = nextDay.toISOString().split('T')[0];

        const alreadyBounded = entries.some(e => e.effective_from === nextDayStr);
        if (!alreadyBounded) {
          // Find the class that was active just before newEffectiveFrom
          const previousClass =
            [...entries]
              .filter((e) => e.effective_from < newEffectiveFrom)
              .sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0]
              ?.cost_class ?? 'Ordinary';

          await api.post(`${API_URL}/api/records`, {
            table: config.table,
            schema: 'public',
            data: {
              [config.fkColumn]: entityId,
              cost_class: previousClass,
              effective_from: nextDayStr,
              notes: t('cat.costClassHistory.restoredAfterPeriod'),
            },
          });
        }
      }

      setAddOpen(false);
      setNewNotes('');
      setNewEffectiveUntil('');
      const updated = await loadHistory();
      await syncCostClassToEntity(updated);
      onHistoryChanged?.();
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to save';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entryId: number) => {
    if (!window.confirm(t('cat.costClassHistory.deleteConfirm'))) return;
    try {
      await api.delete(`${API_URL}/api/records`, {
        data: { table: config.table, schema: 'public', record_id: entryId },
      });
      const updated = await loadHistory();
      await syncCostClassToEntity(updated);
      onHistoryChanged?.();
    } catch (e) {
      console.error('Failed to delete history entry', e);
    }
  };

  const openEdit = (entry: HistoryEntry, nextEntry: HistoryEntry | null) => {
    const idx = entries.findIndex(e => e.id === entry.id);
    const prevEntry = idx > 0 ? entries[idx - 1] : null;
    const nextNextEntry = nextEntry
      ? (entries.find(e => e.effective_from > nextEntry.effective_from) ?? null)
      : null;

    setEditEntry(entry);
    setEditPrevEntry(prevEntry);
    setEditNextEntry(nextEntry);
    setEditNextNextEntry(nextNextEntry);
    setEditCostClass(entry.cost_class);
    setEditEffectiveFrom(entry.effective_from);
    if (nextEntry) {
      const until = new Date(new Date(nextEntry.effective_from).getTime() - 86400000)
        .toISOString().split('T')[0];
      setEditUntil(until);
    } else {
      setEditUntil('');
    }
    setEditNotes(entry.notes || '');
    setEditError(null);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editEntry || !editEffectiveFrom || !editCostClass) return;

    // #4 — validate effective_from against neighbouring entries
    if (editPrevEntry && editEffectiveFrom <= editPrevEntry.effective_from) {
      setEditError(t('cat.costClassHistory.fromAfterPrevious'));
      return;
    }
    if (editNextEntry && editEffectiveFrom >= editNextEntry.effective_from) {
      setEditError(t('cat.costClassHistory.fromBeforeNext'));
      return;
    }

    // Validate: until must be > effective_from
    if (editUntil && editUntil < editEffectiveFrom) {
      setEditError(t('cat.costClassHistory.endBeforeStart'));
      return;
    }

    // #6 — validate that the new nextFrom doesn't collide with N+2
    if (editUntil && editNextEntry && editNextNextEntry) {
      const nextFrom = new Date(new Date(editUntil).getTime() + 86400000).toISOString().split('T')[0];
      if (nextFrom >= editNextNextEntry.effective_from) {
        setEditError(t('cat.costClassHistory.untilConflictsWithNext'));
        return;
      }
    }

    setEditSaving(true);
    setEditError(null);
    try {
      // Update this entry
      await api.put(`${API_URL}/api/records`, {
        table: config.table,
        schema: 'public',
        record_id: editEntry.id,
        data: { cost_class: editCostClass, effective_from: editEffectiveFrom, notes: editNotes || null },
      });

      // Handle the "until" date — it controls the next entry's effective_from
      if (editUntil) {
        const nextFrom = new Date(new Date(editUntil).getTime() + 86400000).toISOString().split('T')[0];
        if (editNextEntry) {
          // Update existing next entry's start date
          await api.put(`${API_URL}/api/records`, {
            table: config.table,
            schema: 'public',
            record_id: editNextEntry.id,
            data: { effective_from: nextFrom },
          });
        } else {
          // No next entry — create a closing entry starting the day after until
          await api.post(`${API_URL}/api/records`, {
            table: config.table,
            schema: 'public',
            data: {
              [config.fkColumn]: entityId,
              cost_class: 'Ordinary',
              effective_from: nextFrom,
              notes: t('cat.costClassHistory.restoredAfterPeriod'),
            },
          });
        }
      } else if (!editUntil && editNextEntry) {
        // #5 — user cleared the until date: delete the next entry (confirmed via warning shown in UI)
        await api.delete(`${API_URL}/api/records`, {
          data: { table: config.table, schema: 'public', record_id: editNextEntry.id },
        });
      }

      setEditOpen(false);
      const updated = await loadHistory();
      await syncCostClassToEntity(updated);
      onHistoryChanged?.();
    } catch (e: any) {
      setEditError(e?.response?.data?.detail || e?.message || 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  };

  const openAdd = () => {
    const today = new Date();
    setNewEffectiveFrom(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    );
    setNewCostClass('Ordinary');
    setNewNotes('');
    setNewEffectiveUntil('');
    setError(null);
    setAddOpen(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <HistoryIcon sx={{ fontSize: 14, color: '#667eea' }} />
          <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
            {t('cat.costClassHistory.title')}
          </Typography>
        </Box>
        <Tooltip title={t('cat.costClassHistory.addPeriod')}>
          <IconButton size="small" onClick={openAdd} sx={{ color: '#667eea', p: 0.25 }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Timeline bar */}
      {!loading && segments.length > 0 && (
        <Box sx={{ mt: 1, mb: 1.5 }}>
          {/* Month labels */}
          <Box sx={{ position: 'relative', height: 16, mb: 0.25 }}>
            {(() => {
              const start = new Date(segments[0].from);
              const end = new Date(segments[segments.length - 1].until);
              const labels: React.ReactNode[] = [];

              // Total number of months in the span, then pick a step so we
              // render at most ~maxLabels evenly-spaced labels (avoids the
              // dense, unreadable overlap on multi-year spans).
              const monthsTotal =
                (end.getFullYear() - start.getFullYear()) * 12 +
                (end.getMonth() - start.getMonth()) + 1;
              const maxLabels = 8;
              const candidateSteps = [1, 2, 3, 6, 12];
              let stepMonths =
                candidateSteps.find((s) => monthsTotal / s <= maxLabels) ??
                12 * Math.ceil(monthsTotal / (maxLabels * 12));

              const cur = new Date(start.getFullYear(), start.getMonth(), 1);
              let monthIndex = 0;
              while (cur <= end) {
                if (monthIndex % stepMonths === 0) {
                  const offsetDays = Math.round((cur.getTime() - start.getTime()) / 86400000);
                  const pct = Math.min(100, Math.max(0, (offsetDays / totalDays) * 100));
                  labels.push(
                    <Typography
                      key={cur.toISOString()}
                      variant="caption"
                      sx={{ position: 'absolute', left: `${pct}%`, fontSize: '0.6rem', color: 'text.secondary', whiteSpace: 'nowrap', transform: 'translateX(-50%)' }}
                    >
                      {cur.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })}
                    </Typography>
                  );
                }
                cur.setMonth(cur.getMonth() + 1);
                monthIndex++;
              }
              return labels;
            })()}
          </Box>

          {/* Colored bar */}
          <Box sx={{ display: 'flex', width: '100%', height: 22, borderRadius: 1, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}>
            {segments.map((seg, i) => (
              <Tooltip
                key={i}
                arrow
                title={
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
                      {translateCostClass(seg.cost_class)}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {formatDate(seg.from)} → {seg.isOpen ? '—' : formatDate(seg.until)}
                    </Typography>
                    <Typography variant="caption">{seg.days}{t('report.daysAbbr')}</Typography>
                  </Box>
                }
              >
                <Box
                  sx={{
                    width: `${(seg.days / totalDays) * 100}%`,
                    minWidth: 4,
                    bgcolor: COST_CLASS_COLORS[seg.cost_class] || '#ab47bc',
                    borderRight: seg.isOpen ? '3px dashed rgba(255,255,255,0.55)' : 'none',
                    cursor: 'default',
                    transition: 'filter 0.15s',
                    '&:hover': { filter: 'brightness(1.18)' },
                  }}
                />
              </Tooltip>
            ))}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
            {Array.from(new Set(segments.map((s) => s.cost_class))).map((cc) => (
              <Box key={cc} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COST_CLASS_COLORS[cc] || '#ab47bc', flexShrink: 0 }} />
                <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{translateCostClass(cc)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {loading ? (
        <CircularProgress size={16} sx={{ ml: 1 }} />
      ) : entries.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('cat.costClassHistory.noHistory')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {entries.map((entry, idx) => {
            const nextEntry = entries[idx + 1];
            const until = nextEntry
              ? formatDate(
                  new Date(new Date(nextEntry.effective_from).getTime() - 86400000)
                    .toISOString()
                    .split('T')[0]
                )
              : '—';
            return (
              <Box
                key={entry.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  background: idx % 2 === 0 ? 'rgba(102,126,234,0.06)' : 'transparent',
                  fontSize: '0.75rem',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                    {translateCostClass(entry.cost_class)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                    {formatDate(entry.effective_from)} → {until}
                  </Typography>
                  {entry.notes && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', fontStyle: 'italic' }}>
                      {translateNote(entry.notes)}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={t('common.edit')}>
                  <IconButton
                    size="small"
                    onClick={() => openEdit(entry, nextEntry ?? null)}
                    sx={{ color: 'primary.light', p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.delete')}>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(entry.id)}
                    sx={{ color: 'error.light', p: 0.25, opacity: 0.6, '&:hover': { opacity: 1 } }}
                  >
                    <DeleteIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Add entry dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1rem' }}>
          {t('cat.costClassHistory.addPeriod')}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1.5 }}>
            <InputLabel>{t('cat.costClass')}</InputLabel>
            <Select
              value={newCostClass}
              label={t('cat.costClass')}
              onChange={(e) => setNewCostClass(e.target.value)}
            >
              {COST_CLASSES.map((cc) => (
                <MenuItem key={cc} value={cc}>{translateCostClass(cc)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateField
            label={t('cat.costClassHistory.effectiveFrom')}
            value={newEffectiveFrom}
            onChange={setNewEffectiveFrom}
            required
            sx={{ mb: 1.5 }}
            fullWidth
          />
          <DateField
            label={t('cat.costClassHistory.effectiveUntil')}
            value={newEffectiveUntil}
            onChange={setNewEffectiveUntil}
            sx={{ mb: 1.5 }}
            fullWidth
          />
          <TextField
            label={t('cat.costClassHistory.notes')}
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button size="small" onClick={() => setAddOpen(false)} disabled={saving}>
            {t('cat.costClassHistory.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleAdd}
            disabled={saving || !newEffectiveFrom || !newCostClass}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {saving ? <CircularProgress size={16} /> : t('cat.costClassHistory.addEntry')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit entry dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, fontSize: '1rem' }}>
          {t('cat.costClassHistory.editEntry')}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          {/* #5 — warn user that clearing Until will delete the next entry */}
          {!editUntil && editNextEntry && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {t('cat.costClassHistory.clearUntilWarning')}
            </Alert>
          )}
          <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1.5 }}>
            <InputLabel>{t('cat.costClass')}</InputLabel>
            <Select
              value={editCostClass}
              label={t('cat.costClass')}
              onChange={(e) => { setEditCostClass(e.target.value); setEditError(null); }}
            >
              {COST_CLASSES.map((cc) => (
                <MenuItem key={cc} value={cc}>{translateCostClass(cc)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <DateField
            label={t('cat.costClassHistory.effectiveFrom')}
            value={editEffectiveFrom}
            onChange={(v) => { setEditEffectiveFrom(v); setEditError(null); }}
            required
            sx={{ mb: 1.5 }}
            fullWidth
          />
          <DateField
            label={t('cat.costClassHistory.effectiveUntil')}
            value={editUntil}
            onChange={(v) => { setEditUntil(v); setEditError(null); }}
            sx={{ mb: 1.5 }}
            fullWidth
          />
          <TextField
            label={t('cat.costClassHistory.notes')}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button size="small" onClick={() => setEditOpen(false)} disabled={editSaving}>
            {t('cat.costClassHistory.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving || !editEffectiveFrom || !editCostClass}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            {editSaving ? <CircularProgress size={16} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CostClassHistoryPanel;
